import React, { useState, useEffect } from 'react';
import { InviteAPI } from '../services/api';
import { Invite } from '../types/invite';
import { LoadingScreen } from './LoadingScreen';
import './InviteList.css';

export const InviteList: React.FC = () => {
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const fetchInvites = async () => {
    try {
      setLoading(true);
      setError('');
      const invitesData = await InviteAPI.getInvites();
      setInvites(invitesData);
    } catch (err: any) {
      setError(err.message || 'Failed to load invites');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvites();
  }, []);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could add a toast notification here
      console.log('Copied to clipboard');
    });
  };

  const handleDeleteInvite = async (inviteId: string, email: string) => {
    if (!window.confirm(`Are you sure you want to revoke the invitation for ${email}? This action cannot be undone.`)) {
      return;
    }

    try {
      setError('');
      await InviteAPI.deleteInvite(inviteId);
      // Refresh the list to show the updated data
      await fetchInvites();
      console.log(`Invitation for ${email} has been revoked`);
    } catch (err: any) {
      setError(err.message || 'Failed to revoke invitation');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  if (loading) {
    return (
      <div className="invite-list-container">
        <div className="loading">Loading invites...</div>
      </div>
    );
  }

  return (
    <div className="invite-list-container">
      <div className="invite-list-header">
        <h2>Sent Invitations</h2>
        <button 
          onClick={fetchInvites}
          className="refresh-button"
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {invites.length === 0 ? (
        <div className="no-invites">
          <p>No invitations found.</p>
        </div>
      ) : (
        <div className="invite-grid">
          {invites.map((invite) => (
            <div key={invite.id} className={`invite-card ${invite.is_used ? 'used' : ''} ${isExpired(invite.expires_at) ? 'expired' : ''}`}>
              <div className="invite-header">
                <span className="invite-email">{invite.email}</span>
                <span className={`invite-status ${invite.is_used ? 'used' : isExpired(invite.expires_at) ? 'expired' : 'pending'}`}>
                  {invite.is_used ? 'Used' : isExpired(invite.expires_at) ? 'Expired' : 'Pending'}
                </span>
              </div>
              
              <div className="invite-details">
                <div className="invite-field">
                  <strong>Role:</strong> {invite.role}
                </div>
                {invite.supervisor_name && (
                  <div className="invite-field">
                    <strong>Supervisor:</strong> {invite.supervisor_name}
                  </div>
                )}
                <div className="invite-field">
                  <strong>Created:</strong> {formatDate(invite.created_at)}
                </div>
                <div className="invite-field">
                  <strong>Expires:</strong> {formatDate(invite.expires_at)}
                </div>
              </div>

              {!invite.is_used && !isExpired(invite.expires_at) && (
                <>
                  <div className="invite-token">
                    <div className="token-field">
                      <strong>Token:</strong>
                      <div className="token-container">
                        <code className="token-value">{invite.token}</code>
                        <button
                          onClick={() => copyToClipboard(invite.token)}
                          className="copy-button"
                          title="Copy to clipboard"
                        >
                          📋
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="invite-actions">
                    <button
                      onClick={() => handleDeleteInvite(invite.id, invite.email)}
                      className="delete-button"
                      title="Revoke invitation"
                    >
                      🗑️ Revoke
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Loading Screen Overlay */}
      <LoadingScreen isVisible={loading} message="Loading invitations..." />
    </div>
  );
};